import { CircularProgress } from '@mui/material';
import {
    OrganizationSwitchModal,
    UMButton,
    type OrganizationSwitchModalOrganization,
    type OrganizationSwitchModalOrganizationId,
} from '@urbanmetrix/ui-kit';
import React, { useCallback, useEffect, useState } from 'react';
import { AuthenticationStatus, useAppPhaseStore } from '../store/AppPhaseStore';
import { useEnvStore } from '../store/EnvStore';
import { listOrganizations, setActiveOrganization, useSessionStore } from '../store/SessionStore';
import { getViewer } from '../store/viewerStore';
import usePersistentStore from './store/persistent/persistentStore';
import ErrorMessage from './components/errorMessage';

interface AuthProps {
    children?: React.ReactNode;
}

const Auth: React.FC<AuthProps> = (props) => {
    const authStatus = useAppPhaseStore((state) => state.authStatus);
    const authenticationError = useAppPhaseStore((state) => state.authError);
    const authenticationErrorStatus = useAppPhaseStore((state) => state.authErrorStatus);
    const user = useSessionStore((state) => state.user);
    const webappUrl = useEnvStore((state) => state.backendUrl);
    const isAuthenticated = useSessionStore((state) => state.status === 'authenticated');
    const token = useSessionStore((state) => state.token);
    const fpsIndicatorVisible = usePersistentStore((state) => state.global.overlays.fpsIndicatorVisible);
    const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    const [organizationModalOpen, setOrganizationModalOpen] = useState(false);
    const [isSwitchingOrganization, setIsSwitchingOrganization] = useState(false);

    const isGridNotFound = authStatus === AuthenticationStatus.ERROR && authenticationError === 'GRID_NOT_FOUND';
    const isOwnerWithoutActivePlan = authStatus === AuthenticationStatus.ERROR && authenticationError === 'NO_ACTIVE_PLAN_OWNER';
    const isMemberWithoutActivePlan = authStatus === AuthenticationStatus.ERROR && authenticationError === 'NO_ACTIVE_PLAN_MEMBER';
    const isMaintenanceError = authStatus === AuthenticationStatus.ERROR && authenticationErrorStatus === 503;
    const shouldShowStatusMessage =
        authStatus === AuthenticationStatus.NOT_AUTHENTICATED ||
        authStatus === AuthenticationStatus.NO_API ||
        authStatus === AuthenticationStatus.ERROR ||
        authStatus === AuthenticationStatus.WRONG_ACCOUNT;
    const billingPageUrl = `${webappUrl}/account/subscription`;

    useEffect(() => {
        if (shouldShowStatusMessage) {
            getViewer().setStatsVisibility(false);
            return;
        }
        getViewer().setStatsVisibility(fpsIndicatorVisible);
    }, [shouldShowStatusMessage, fpsIndicatorVisible]);

    const loadAvailableOrganizations = useCallback(async (): Promise<OrganizationSwitchModalOrganization[]> => {
        if (!isAuthenticated) {
            return [];
        }

        const organizations = await listOrganizations(token);
        return Array.isArray(organizations)
            ? organizations.map((organization) => ({
                  id: organization.id,
                  name: organization.name || organization.slug || 'Organization',
                  slug: organization.slug,
              }))
            : [];
    }, [isAuthenticated, token]);

    const handleSwitchOrganization = useCallback(
        async (organizationId: OrganizationSwitchModalOrganizationId) => {
            if (!organizationId || String(organizationId) === String(user.account_id)) {
                setOrganizationModalOpen(false);
                return;
            }

            try {
                setIsSwitchingOrganization(true);
                await setActiveOrganization(String(organizationId), token);
                window.location.reload();
            } finally {
                setIsSwitchingOrganization(false);
            }
        },
        [user.account_id, token]
    );

    switch (authStatus) {
        case AuthenticationStatus.INITIALIZING:
            return (
                <CircularProgress
                    size={100}
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                    }}
                />
            );

        case AuthenticationStatus.AUTHENTICATED:
            return <>{props.children}</>;

        case AuthenticationStatus.NOT_AUTHENTICATED: {
            if (isLocalhost && authenticationError === 'LOCAL_LOGIN_MISSING_CREDENTIALS') {
                return <ErrorMessage message="Authentication failed. Add VITE_LOGIN_EMAIL and VITE_LOGIN_PASS to .env." />;
            }
            if (isLocalhost && authenticationError === 'LOCAL_LOGIN_FAILED') {
                return <ErrorMessage message="Authentication failed. Check VITE_LOGIN_EMAIL and VITE_LOGIN_PASS." />;
            }
            const redirectTo = `${window.location.pathname}${window.location.search}`;
            // ResourceStore.gridId is still empty at this point because resources haven't loaded yet (auth failed first).
            // Use the grid id parsed from the URL query so sign-in can redirect back to the correct grid.
            const gridIdFromQuery = useEnvStore.getState().gridIdFromQuery;
            const params = new URLSearchParams({
                grid_id: gridIdFromQuery,
                redirectTo,
            });
            window.location.href = `/login?${params.toString()}`;
            return <ErrorMessage message="Redirecting..." />;
        }
        case AuthenticationStatus.NO_API:
            return <ErrorMessage message="No API available. Backend is probably offline." />;
        case AuthenticationStatus.ERROR:
            if (isOwnerWithoutActivePlan) {
                return (
                    <ErrorMessage message="No active plan was found for the active organization. Subscribe to a plan to continue.">
                        <UMButton
                            text="Open billing page"
                            color="white"
                            variant="contained"
                            onClick={() => window.open(billingPageUrl, '_blank', 'noopener,noreferrer')}
                            className="auth-organization-switch-button"
                        />
                    </ErrorMessage>
                );
            }
            if (isMemberWithoutActivePlan) {
                return (
                    <ErrorMessage message="No active plan was found for the active organization. Contact the organization owner to subscribe to a plan." />
                );
            }
            if (isMaintenanceError) {
                return (
                    <ErrorMessage message="The 3D Visualizer is currently under maintenance. Please try again later.">
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 16,
                                marginTop: 16,
                            }}
                        >
                            <div className="auth-error-message-text">For further details, please check project app.</div>
                            <UMButton
                                text="Check"
                                color="white"
                                variant="contained"
                                className="auth-organization-switch-button"
                                onClick={() => window.open(`${webappUrl}`, '_blank', 'noreferrer')}
                            />
                        </div>
                    </ErrorMessage>
                );
            }
            if (isGridNotFound) {
                return (
                    <>
                        <ErrorMessage
                            message="This grid doesn't exist in the active organization. Switch organization to try again."
                            style={{ zIndex: 1200 }}
                        >
                            <UMButton
                                text="Switch organization"
                                color="white"
                                variant="contained"
                                onClick={() => setOrganizationModalOpen(true)}
                                className="auth-organization-switch-button"
                            />
                        </ErrorMessage>
                        <OrganizationSwitchModal
                            open={organizationModalOpen}
                            onClose={() => setOrganizationModalOpen(false)}
                            activeOrganizationId={user.account_id}
                            isSwitchingOrganization={isSwitchingOrganization}
                            loadOrganizations={loadAvailableOrganizations}
                            onSwitchOrganization={handleSwitchOrganization}
                            texts={{
                                subtitle: 'Select the organization where this grid exists.',
                                emptyState: 'No other organizations available.',
                            }}
                        />
                    </>
                );
            }
            return <ErrorMessage message="Server Error" />;
        case AuthenticationStatus.WRONG_ACCOUNT:
            return <ErrorMessage message="Wrong account" />;
        default:
            throw new Error('Unknown authentication status');
    }
};

export default Auth;
